
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Redirect, StatusCode } from "@/types/redirectTypes";
import { useRedirectForm } from "@/hooks/useRedirectForm";

interface RedirectFormProps {
  redirect?: Redirect;
  onSuccess: (redirect: Redirect) => void;
  onCancel: () => void;
}

const RedirectForm: React.FC<RedirectFormProps> = ({
  redirect,
  onSuccess,
  onCancel,
}) => {
  const {
    sourcePath,
    setSourcePath,
    targetPath,
    setTargetPath,
    statusCode,
    setStatusCode,
    isActive,
    setIsActive,
    notes,
    setNotes,
    isSubmitting,
    validationErrors,
    handleSubmit,
  } = useRedirectForm({
    initialData: redirect,
    onSuccess,
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="sourcePath">
            Source Path <span className="text-red-500">*</span>
          </Label>
          <Input
            id="sourcePath"
            placeholder="/old-post-url-2025"
            value={sourcePath}
            onChange={(e) => setSourcePath(e.target.value)}
            className={validationErrors.sourcePath ? "border-red-500" : ""}
          />
          {validationErrors.sourcePath && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.sourcePath}</p>
          )}
          <p className="text-sm text-gray-500">
            The old URL path that will be redirected. Must start with a forward slash (/).
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="targetPath">
            Target Path <span className="text-red-500">*</span>
          </Label>
          <Input
            id="targetPath"
            placeholder="/new-post-url-2026"
            value={targetPath}
            onChange={(e) => setTargetPath(e.target.value)}
            className={validationErrors.targetPath ? "border-red-500" : ""}
          />
          {validationErrors.targetPath && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.targetPath}</p>
          )}
          <p className="text-sm text-gray-500">
            The new URL path to redirect to. Must start with a forward slash (/).
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="statusCode">Status Code</Label>
            <Select 
              value={statusCode.toString()} 
              onValueChange={(value) => setStatusCode(parseInt(value) as StatusCode)}
            >
              <SelectTrigger id="statusCode">
                <SelectValue placeholder="Select status code" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="301">301 - Permanent Redirect</SelectItem>
                <SelectItem value="302">302 - Temporary Redirect</SelectItem>
                <SelectItem value="307">307 - Temporary Redirect (same method)</SelectItem>
                <SelectItem value="308">308 - Permanent Redirect (same method)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-gray-500">
              301 (permanent) is recommended for SEO purposes.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="isActive">Status</Label>
            <div className="flex items-center space-x-2 pt-2">
              <Switch 
                id="isActive" 
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <Label htmlFor="isActive" className="cursor-pointer">
                {isActive ? "Active" : "Inactive"}
              </Label>
            </div>
            <p className="text-sm text-gray-500">
              Inactive redirects will not be applied.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            placeholder="Add any notes about this redirect (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
          <p className="text-sm text-gray-500">
            Optional notes for your reference (not visible to users).
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : redirect ? "Update Redirect" : "Create Redirect"}
        </Button>
      </div>
    </form>
  );
};

export default RedirectForm;
